// frontend/src/reducers/orderReducers.js

import * as actionTypes from "../constants/orderConstants";

function createAsyncState(extra = {}) {
  return {
    loading: false,
    success: false,
    error: null,
    errorCode: "",
    status: 0,
    requestId: "",
    retryAfterSeconds: 0,
    message: "",
    ...extra,
  };
}

function createInitialState() {
  return {
    create: createAsyncState({
      order: null,
      orderNumber: "",
      reused: false,
      ignored: false,
      idempotencyKey: "",
      issues: [],
      failures: [],
      inventoryFailures: [],
    }),

    listMy: createAsyncState({
      orders: [],
      page: 1,
      limit: 20,
      pages: 1,
      count: 0,
      pagination: null,
    }),

    details: createAsyncState({
      order: null,
    }),

    status: createAsyncState({
      order: null,
      orderNumber: "",
      orderStatus: "",
      paymentStatus: "",
      customerCheckout: null,
    }),

    cancel: createAsyncState({
      order: null,
      paymentAction: null,
      refundAction: null,
      eligibility: null,
      idempotencyKey: "",
    }),
  };
}

const initialState = createInitialState();

function safeText(value = "") {
  return String(value ?? "").trim();
}

function customerSafeMessage(value = "", fallback = "") {
  const text = safeText(value);

  if (!text) return fallback;

  const lower = text.toLowerCase();

  if (
    lower.includes("reserved") ||
    lower.includes("temporarily reserved") ||
    lower.includes("allocated") ||
    lower.includes("held") ||
    lower.includes("held for")
  ) {
    return fallback;
  }

  return text;
}

function requestBranch(branch = {}, extra = {}) {
  return {
    ...branch,
    loading: true,
    success: false,
    error: null,
    errorCode: "",
    status: 0,
    requestId: "",
    retryAfterSeconds: 0,
    message: "",
    ...extra,
  };
}

function successBranch(branch = {}, payload = {}, extra = {}) {
  return {
    ...branch,
    loading: false,
    success: true,
    error: null,
    errorCode: "",
    status: 0,
    requestId: "",
    retryAfterSeconds: 0,
    message: customerSafeMessage(payload?.message, ""),
    ...extra,
  };
}

function failBranch(branch = {}, payload = {}) {
  return {
    ...branch,
    loading: false,
    success: false,
    error: customerSafeMessage(payload?.message, "Request failed."),
    errorCode: safeText(payload?.code),
    status: Number(payload?.status || 0),
    requestId: safeText(payload?.requestId),
    retryAfterSeconds: Number(payload?.retryAfterSeconds || 0),
    message: "",
  };
}

function getOrderFromPayload(payload = {}) {
  return payload?.order || null;
}

function getOrdersFromPayload(payload = {}) {
  if (Array.isArray(payload?.orders)) return payload.orders;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;

  return [];
}

function getPaginationFromPayload(payload = {}) {
  return payload?.pagination || null;
}

function getOrderNumberFromPayload(payload = {}) {
  return safeText(payload?.orderNumber || payload?.order?.orderNumber);
}

function getOrderIdentity(order = {}) {
  return safeText(order?.orderNumber || order?._id || order?.id);
}

function mergeOrderBranches(state = {}, order = null) {
  if (!order) return state;

  const updatedIdentity = getOrderIdentity(order);
  const detailsIdentity = getOrderIdentity(state.details?.order);

  return {
    ...state,
    listMy: {
      ...state.listMy,
      orders: Array.isArray(state.listMy?.orders)
        ? state.listMy.orders.map((item) =>
            getOrderIdentity(item) === updatedIdentity ? order : item,
          )
        : [],
    },
    details: {
      ...state.details,
      order:
        updatedIdentity && updatedIdentity === detailsIdentity
          ? order
          : state.details?.order || null,
    },
  };
}

function getPageValue(payload = {}, fallback = 1) {
  return Number(payload?.pagination?.page || payload?.page || fallback);
}

function getLimitValue(payload = {}, fallback = 20) {
  return Number(payload?.pagination?.limit || payload?.limit || fallback);
}

function getPagesValue(payload = {}, fallback = 1) {
  return Number(
    payload?.pagination?.totalPages ||
      payload?.pages ||
      payload?.totalPages ||
      fallback,
  );
}

function getCountValue(payload = {}, fallback = 0) {
  return Number(
    payload?.pagination?.total || payload?.count || payload?.total || fallback,
  );
}

export function orderReducer(state = initialState, action) {
  switch (action.type) {
    case actionTypes.ORDER_CREATE_REQUEST:
      return {
        ...state,
        create: requestBranch(state.create, {
          issues: [],
          failures: [],
          inventoryFailures: [],
        }),
      };

    case actionTypes.ORDER_CREATE_SUCCESS:
      return {
        ...state,
        create: successBranch(state.create, action.payload, {
          order: getOrderFromPayload(action.payload),
          orderNumber: getOrderNumberFromPayload(action.payload),
          reused: Boolean(action.payload?.reused),
          ignored: Boolean(action.payload?.ignored),
          idempotencyKey: safeText(action.payload?.idempotencyKey),
          issues: [],
          failures: [],
          inventoryFailures: [],
        }),
      };

    case actionTypes.ORDER_CREATE_FAIL:
      return {
        ...state,
        create: {
          ...failBranch(state.create, action.payload),
          issues: Array.isArray(action.payload?.issues)
            ? action.payload.issues
            : [],
          failures: Array.isArray(action.payload?.failures)
            ? action.payload.failures
            : [],
          inventoryFailures: Array.isArray(action.payload?.inventoryFailures)
            ? action.payload.inventoryFailures
            : [],
        },
      };

    case actionTypes.ORDER_CREATE_RESET:
      return {
        ...state,
        create: initialState.create,
      };

    case actionTypes.ORDER_LIST_MY_REQUEST:
      return {
        ...state,
        listMy: requestBranch(state.listMy),
      };

    case actionTypes.ORDER_LIST_MY_SUCCESS:
      return {
        ...state,
        listMy: successBranch(state.listMy, action.payload, {
          orders: getOrdersFromPayload(action.payload),
          page: getPageValue(action.payload, 1),
          limit: getLimitValue(action.payload, 20),
          pages: getPagesValue(action.payload, 1),
          count: getCountValue(action.payload, 0),
          pagination: getPaginationFromPayload(action.payload),
        }),
      };

    case actionTypes.ORDER_LIST_MY_FAIL:
      return {
        ...state,
        listMy: failBranch(state.listMy, action.payload),
      };

    case actionTypes.ORDER_LIST_MY_RESET:
      return {
        ...state,
        listMy: initialState.listMy,
      };

    case actionTypes.ORDER_DETAILS_REQUEST:
      return {
        ...state,
        details: requestBranch(state.details),
      };

    case actionTypes.ORDER_DETAILS_SUCCESS:
      return {
        ...state,
        details: successBranch(state.details, action.payload, {
          order: getOrderFromPayload(action.payload),
        }),
      };

    case actionTypes.ORDER_DETAILS_FAIL:
      return {
        ...state,
        details: failBranch(state.details, action.payload),
      };

    case actionTypes.ORDER_DETAILS_RESET:
      return {
        ...state,
        details: initialState.details,
      };

    case actionTypes.ORDER_STATUS_REQUEST:
      return {
        ...state,
        status: requestBranch(state.status),
      };

    case actionTypes.ORDER_STATUS_SUCCESS: {
      const order = getOrderFromPayload(action.payload);

      return mergeOrderBranches(
        {
          ...state,
          status: successBranch(state.status, action.payload, {
            order,
            orderNumber: getOrderNumberFromPayload(action.payload),
            orderStatus: safeText(
              action.payload?.status ||
                action.payload?.orderStatus ||
                order?.status,
            ),
            paymentStatus: safeText(
              action.payload?.paymentStatus || order?.payment?.status,
            ),
            customerCheckout:
              action.payload?.customerCheckout ||
              order?.customerCheckout ||
              null,
          }),
        },
        order,
      );
    }

    case actionTypes.ORDER_STATUS_FAIL:
      return {
        ...state,
        status: failBranch(state.status, action.payload),
      };

    case actionTypes.ORDER_STATUS_RESET:
      return {
        ...state,
        status: initialState.status,
      };

    case actionTypes.ORDER_CANCEL_REQUEST:
      return {
        ...state,
        cancel: requestBranch(state.cancel),
      };

    case actionTypes.ORDER_CANCEL_SUCCESS: {
      const order = getOrderFromPayload(action.payload);

      return mergeOrderBranches(
        {
          ...state,
          cancel: successBranch(state.cancel, action.payload, {
            order,
            paymentAction: action.payload?.paymentAction || null,
            refundAction: action.payload?.refundAction || null,
            eligibility: action.payload?.eligibility || null,
            idempotencyKey: safeText(action.payload?.idempotencyKey),
          }),
        },
        order,
      );
    }

    case actionTypes.ORDER_CANCEL_FAIL:
      return {
        ...state,
        cancel: failBranch(state.cancel, action.payload),
      };

    case actionTypes.ORDER_CANCEL_RESET:
      return {
        ...state,
        cancel: initialState.cancel,
      };

    case actionTypes.ORDER_RESET_ERROR:
      return {
        ...state,
        create: { ...state.create, error: null, errorCode: "", status: 0 },
        listMy: { ...state.listMy, error: null, errorCode: "", status: 0 },
        details: { ...state.details, error: null, errorCode: "", status: 0 },
        status: { ...state.status, error: null, errorCode: "", status: 0 },
        cancel: { ...state.cancel, error: null, errorCode: "", status: 0 },
      };

    case actionTypes.ORDER_RESET_META:
      return {
        ...state,
        create: {
          ...state.create,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        listMy: {
          ...state.listMy,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        details: {
          ...state.details,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        status: {
          ...state.status,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        cancel: {
          ...state.cancel,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
      };

    case actionTypes.ORDER_RESET_STATE:
      return createInitialState();

    default:
      return state;
  }
}

export default orderReducer;
